import React, { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { ApiService } from '../services/api';
// import { SignedTransactionService } from '../services/signedTransactionService';
import { Clock, Check, ArrowRight } from 'lucide-react';

interface GovernanceStats {
  current_fee_rate: number;
  active_proposals: number;
  treasury_balance: string;
  total_locked_tokens: string;
  total_voting_power: string;
  is_loop_token_available?: boolean;
  loop_token_message?: string;
}

// interface UserVotingPower {
//   voting_power: string;
//   locked_amount: string;
//   expires_at: string | null;
//   is_locked: boolean;
//   is_loop_token_available?: boolean;
//   message?: string;
// }

interface Proposal {
  id: string;
  title: string;
  description: string;
  status: 'voting' | 'passed' | 'rejected';
  forPercentage: number;
  againstPercentage: number;
  abstainPercentage: number;
  timeRemaining?: string;
  passedDate?: string;
}

interface TreasuryAllocation {
  name: string;
  percentage: number;
  color: string;
}

export const Governance: React.FC = () => {
  // const { publicKey, connected } = useWallet();
  const { connected } = useWallet();
  const [stats, setStats] = useState<GovernanceStats | null>(null);
  // const [userVotingPower, setUserVotingPower] = useState<UserVotingPower | null>(null);
  const [loading, setLoading] = useState(true);
  // const [lockAmount, setLockAmount] = useState('');
  // const [lockDuration, setLockDuration] = useState('2592000'); // 1 month in seconds
  // const [isLocking, setIsLocking] = useState(false);

  // Mock proposals data matching the image
  const proposals: Proposal[] = [
    {
      id: '1',
      title: 'Adjust Settlement Fee to 0.07%',
      description: 'Reduce fee rate by 1 basis point to improve competitiveness while maintaining treasury sustainability. Impact assessment shows minimal revenue reduction.',
      status: 'voting',
      forPercentage: 67,
      againstPercentage: 25,
      abstainPercentage: 8,
      timeRemaining: '2d 4h',
    },
    {
      id: '2',
      title: 'Approve Tokenized Real Estate as Stored Value Asset',
      description: 'Expand asset class support to include tokenized real estate securities. Requires additional compliance verification layer and custody protocols.',
      status: 'voting',
      forPercentage: 42,
      againstPercentage: 50,
      abstainPercentage: 8,
      timeRemaining: '5d 12h',
    },
    {
      id: '3',
      title: 'Q4 Development Grant Allocation',
      description: 'Allocate $420K from treasury to ecosystem development grants. Focus areas: cross-chain infrastructure, security tooling, and enterprise integrations.',
      status: 'passed',
      forPercentage: 83,
      againstPercentage: 8,
      abstainPercentage: 9,
      passedDate: 'Oct 12, 2025',
    },
  ];

  // Treasury allocation data
  const treasuryAllocations: TreasuryAllocation[] = [
    { name: 'Operational Fund', percentage: 40, color: 'bg-blue-500' },
    { name: 'Development Grants', percentage: 30, color: 'bg-blue-400' },
    { name: 'Staker Distribution', percentage: 20, color: 'bg-blue-300' },
    { name: 'Buyback Allocation', percentage: 10, color: 'bg-red-400' },
  ];

  useEffect(() => {
    // if (connected) {
    //   loadGovernanceData();
    // }
    loadGovernanceData();
  }, [connected]);

  const loadGovernanceData = async () => {
    try {
      setLoading(true);
      // const [statsData, votingPowerData] = await Promise.all([
      //   ApiService.getGovernanceStats(),
      //   ApiService.getUserVotingPower()
      // ]);
      
      const statsData = await ApiService.getGovernanceStats();
      setStats(statsData);
      // setUserVotingPower(votingPowerData);
    } catch (error) {
      console.error('Failed to load governance data:', error);
    } finally {
      setLoading(false);
    }
  };

  // const handleLockTokens = async () => {
  //   if (!publicKey || !lockAmount) return;

  //   try {
  //     setIsLocking(true);
      
  //     // Build lock transaction
  //     const buildResponse = await ApiService.buildLockTokensTransaction({
  //       amount: parseInt(lockAmount),
  //       duration: parseInt(lockDuration)
  //     });

  //     // Sign and send transaction
  //     const result = await SignedTransactionService.signAndSendTransaction(
  //       buildResponse.transaction,
  //       null // wallet will be handled by the service
  //     );

  //     if (result.success) {
  //       // Save lock result
  //       const expiresAt = new Date();
  //       expiresAt.setSeconds(expiresAt.getSeconds() + parseInt(lockDuration));
        
  //       await ApiService.saveLockResult({
  //         amount: lockAmount,
  //         duration: lockDuration,
  //         votingPower: '0', // Will be calculated by backend
  //         expiresAt: expiresAt.toISOString()
  //       });

  //       alert('Tokens locked successfully!');
  //       setLockAmount('');
  //       loadGovernanceData();
  //     } else {
  //       alert('Failed to lock tokens: ' + result.error);
  //     }
  //   } catch (error) {
  //     console.error('Failed to lock tokens:', error);
  //     alert('Failed to lock tokens: ' + error);
  //   } finally {
  //     setIsLocking(false);
  //   }
  // };

  // const formatDuration = (seconds: number) => {
  //   const days = Math.floor(seconds / 86400);
  //   const months = Math.floor(days / 30);
  //   const years = Math.floor(months / 12);
    
  //   if (years > 0) return `${years} year${years > 1 ? 's' : ''}`;
  //   if (months > 0) return `${months} month${months > 1 ? 's' : ''}`;
  //   return `${days} day${days > 1 ? 's' : ''}`;
  // };

  const formatNumber = (num: string) => {
    return new Intl.NumberFormat().format(parseInt(num));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Loading governance data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Key Performance Indicators */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4 sm:mb-6">Key Performance Indicators</h1>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-[#182233] rounded-lg p-4 sm:p-6 border border-gray-700">
            <p className="text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Current Fee Rate</p>
            <p className="text-xl sm:text-2xl font-bold text-white">
              {(stats?.current_fee_rate || 0) * 100}%
            </p>
          </div>
          
          <div className="bg-[#182233] rounded-lg p-4 sm:p-6 border border-gray-700">
            <p className="text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Active Proposal</p>
            <p className="text-xl sm:text-2xl font-bold text-white">
              {stats?.active_proposals || 0}
            </p>
          </div>
          
          <div className="bg-[#182233] rounded-lg p-4 sm:p-6 border border-gray-700">
            <p className="text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Treasure Balance</p>
            <p className="text-xl sm:text-2xl font-bold text-white">
              ${formatNumber(stats?.treasury_balance || '0')}
            </p>
          </div>
          
          <div className="bg-[#182233] rounded-lg p-4 sm:p-6 border border-gray-700">
            <p className="text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Total Locked</p>
            <p className="text-xl sm:text-2xl font-bold text-white">
              {formatNumber(stats?.total_locked_tokens || '0')} LOOP
            </p>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Live Governance Decisions */}
        <div className="lg:col-span-2 bg-[#182233] rounded-lg p-4 sm:p-6 border border-gray-700">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Live Governance Decisions</h2>
          
          <div className="space-y-4">
            {proposals.map((proposal) => (
              <div key={proposal.id} className="bg-[#0E131A] rounded-lg p-4 border border-gray-700">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-base sm:text-lg font-semibold text-white pr-4">{proposal.title}</h3>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                    proposal.status === 'voting' 
                      ? 'bg-blue-900/50 text-blue-400 border border-blue-500/50' 
                      : 'bg-green-900/50 text-green-400 border border-green-500/50'
                  }`}>
                    {proposal.status === 'voting' ? 'VOTING' : 'PASSED'}
                  </span>
                </div>
                
                <p className="text-sm text-gray-400 mb-4">{proposal.description}</p>
                
                {/* Progress Bar */}
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden flex mb-3">
                  <div 
                    className="bg-blue-500 h-full"
                    style={{ width: `${proposal.forPercentage}%` }}
                  />
                  <div 
                    className="bg-red-500 h-full"
                    style={{ width: `${proposal.againstPercentage}%` }}
                  />
                  <div 
                    className="bg-gray-500 h-full"
                    style={{ width: `${proposal.abstainPercentage}%` }}
                  />
                </div>
                
                {/* Voting Stats */}
                <div className="flex flex-wrap items-center justify-between text-xs sm:text-sm">
                  <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                    <span className="flex items-center">
                      <span className="w-2 h-2 rounded-full bg-blue-500 mr-1.5"></span>
                      <span className="text-gray-400">For: {proposal.forPercentage}%</span>
                    </span>
                    <span className="flex items-center">
                      <span className="w-2 h-2 rounded-full bg-red-500 mr-1.5"></span>
                      <span className="text-gray-400">Against: {proposal.againstPercentage}%</span>
                    </span>
                    <span className="flex items-center">
                      <span className="w-2 h-2 rounded-full bg-gray-500 mr-1.5"></span>
                      <span className="text-gray-400">Abstain: {proposal.abstainPercentage}%</span>
                    </span>
                  </div>
                  
                  <div className="flex items-center text-gray-400 mt-2 sm:mt-0">
                    {proposal.status === 'voting' ? (
                      <>
                        <Clock className="w-4 h-4 mr-1" />
                        <span>Voting closes in {proposal.timeRemaining}</span>
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-1 text-green-400" />
                        <span>Passed on {proposal.passedDate}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-sm text-gray-500 italic mt-4 text-center">
            All proposals recorded on-chain - visible, auditable, accountable
          </p>
        </div>

        {/* Treasury Utilization & Fee Flow */}
        <div className="bg-[#182233] rounded-lg p-4 sm:p-6 border border-gray-700">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Treasury utilization &<br />Fee Flow</h2>
          
          {/* Treasury Reserve */}
          <div className="bg-[#0E131A] rounded-lg p-6 border border-gray-700 mb-6 text-center">
            <p className="text-sm text-gray-400 mb-2">Treasury Reserve</p>
            <p className="text-3xl sm:text-4xl font-bold text-[#2777EF]">$4.2M</p>
          </div>
          
          {/* Allocations */}
          <div className="space-y-4 mb-6">
            {treasuryAllocations.map((allocation, index) => (
              <div key={index}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">{allocation.name}</span>
                  <span className="text-gray-400">%{allocation.percentage}</span>
                </div>
                <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${allocation.color}`}
                    style={{ width: `${allocation.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          
          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-6">
            <button className="flex items-center gap-1 px-3 py-1.5 bg-[#2777EF] text-white text-xs sm:text-sm rounded-full">
              Protocol Fees
            </button>
            <button className="flex items-center gap-1 px-3 py-1.5 bg-[#0E131A] text-gray-300 text-xs sm:text-sm rounded-full border border-gray-600">
              <ArrowRight className="w-3 h-3" />
              Treasury Buckets
            </button>
            <button className="flex items-center gap-1 px-3 py-1.5 bg-[#0E131A] text-gray-300 text-xs sm:text-sm rounded-full border border-gray-600">
              <ArrowRight className="w-3 h-3" />
              Distribution Channels
            </button>
          </div>
          
          {/* Fee Info */}
          <div className="bg-[#0E131A] rounded-lg p-4 border border-gray-700">
            <p className="text-sm text-gray-400 italic">
              Every 0.08% fee recycled back into the network's growth.
            </p>
          </div>
        </div>
      </div>

      {/* Protocol Governance Overview */}
      <div className="bg-[#182233] rounded-lg p-4 sm:p-6 border border-gray-700">
        <h2 className="text-xl sm:text-2xl font-bold text-white mb-6">Protocol Governance Overview</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          {/* Circular Chart */}
          <div className="flex justify-center">
            <div className="relative w-48 h-48 sm:w-56 sm:h-56">
              {/* Background Circle */}
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#1E293B"
                  strokeWidth="8"
                />
                {/* Progress Circle - showing ~85% filled */}
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#2777EF"
                  strokeWidth="8"
                  strokeDasharray={`${85 * 2.51} ${100 * 2.51}`}
                  strokeLinecap="round"
                />
                {/* Small orange accent */}
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#F97316"
                  strokeWidth="8"
                  strokeDasharray={`${5 * 2.51} ${100 * 2.51}`}
                  strokeDashoffset={`${-85 * 2.51}`}
                  strokeLinecap="round"
                />
              </svg>
              {/* Center Text */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-4xl sm:text-5xl font-bold text-[#2777EF]">12</span>
                <span className="text-sm sm:text-base text-[#2777EF]">Members</span>
              </div>
            </div>
          </div>
          
          {/* Stats */}
          <div className="space-y-4">
            <div className="bg-[#0E131A] rounded-lg p-4 border border-gray-700 flex justify-between items-center">
              <span className="text-gray-400">Fee Rate</span>
              <span className="text-lg sm:text-xl font-semibold text-white">0.08%</span>
            </div>
            
            <div className="bg-[#0E131A] rounded-lg p-4 border border-gray-700 flex justify-between items-center">
              <span className="text-gray-400">Governance Phase</span>
              <span className="text-base sm:text-lg font-semibold text-white">Phase 1 - shared control</span>
            </div>
            
            <div className="bg-[#0E131A] rounded-lg p-4 border border-gray-700 flex justify-between items-center">
              <span className="text-gray-400">Next Proposal Cycle</span>
              <span className="text-lg sm:text-xl font-semibold text-white">5 days</span>
            </div>
            
            <div className="bg-[#0E131A] rounded-lg p-4 border border-gray-700 flex justify-between items-center">
              <span className="text-gray-400">Decision Threshold</span>
              <span className="text-lg sm:text-xl font-semibold text-white">3 of 5 multisig</span>
            </div>
          </div>
        </div>
        
        <p className="text-sm sm:text-base text-[#2777EF] italic text-center mt-6">
          Decisions shaped by those settle value - not by speculators.
        </p>
      </div>

      {/* Footer */}
      <div className="text-center text-gray-400 text-sm py-4">
        © 2025 LoopChain
      </div>
    </div>
  );
};
